import React, { act, useEffect, useState } from "react"
import TextInput from "../../components/commons/TextInput"
import TextAreaInput from "../../components/commons/TextAreaInput"
import Dropdown from "../../components/commons/Dropdown"
import UploadInput from "../../components/commons/UploadInput"
import { getAllCategories, getCategoryById } from "../../firebase/categories"
import { toast } from "react-toastify"
import { createListing, deleteListing, getListingByID, updateListing } from "../../firebase/listings"
import { useNavigate, useParams } from "react-router-dom"
import { useAuth } from "../../context/authContext"
import { useAtom } from "jotai"
import { userAtom } from "../../store"
import { brands, conditions, rarities, boxTypes } from "../../utils/data"
import Breadcrumb from "../../components/Breadcrumb"
import DialogConfirmation from "../../components/dialogs/DialogConfirmation"

const MarketplaceEditListing = () => {
    const { cardId } = useParams()
    const [categories, setCategories] = useState([])
    const [subCategories, setSubCategories] = useState([])
    const [title, setTitle] = useState("")
    const [brand, setBrand] = useState(brands[0].value)
    const [condition, setCondition] = useState(conditions[0].value)
    const [rarity, setRarity] = useState(rarities[0].value)
    const [boxType, setBoxType] = useState(boxTypes[0].value)
    const [price, setPrice] = useState(null)
    const [originalPrice, setOriginalPrice] = useState(null)
    const [pictures, setPictures] = useState([])
    const [category, setCategory] = useState("")
    const [subCategory, setSubCategory] = useState("")
    const [description, setDescription] = useState("")
    const [card, setCard] = useState(null)
    const [images, setImages] = useState([])
    const { user } = useAuth()
    const [userData, setUserData] = useAtom(userAtom)
    const [activeCreate, setActiveCreate] = useState(false)
    const [quantity, setQuantity] = useState(1)
    const [releasedYear, setReleasedYear] = useState(null)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
    const [showDeleteConfirmedModal, setShowDeleteConfirmedModal] = useState(false)
    const paymentOptions = [
        { label: "Card (Visa, Mastercard, Apple Pay, Google Pay)", value: "Card" },
        { label: "PayPal", value: "PayPal" },
        { label: "ACH Bank Debit", value: "ACH Bank Debit" },
        { label: "Bank Wire (SWIFT)", value: "Bank Wire" },
    ]
    const [paymentMethods, setPaymentMethods] = useState([])

    const navigate = useNavigate()

    useEffect(() => {
        getAllCategories().then((cats) => {
            const catOptions = []

            cats.forEach((cat) => {
                catOptions.push({ value: cat.id, label: cat.name.toUpperCase() })
            })

            if (cats.length > 0)
                setCategory(cats[0].id)

            setCategories(catOptions)
        })
    }, [])

    useEffect(() => {
        if (category) {
            getCategoryById(category).then((data) => {
                if (data && data.subCategories) {
                    setSubCategories(generateSubCategoryOptions(data))
                }
            })
        }
    }, [category])

    useEffect(() => {
        getListingByID(cardId).then((item) => {
            console.log("Loaded listing data:", item)
            if (item) {
                setCard(item)
                setCategory(item.category || '')
                setSubCategory(item.sub_category || '')
                setTitle(item.title || '')
                setPrice(item.price || 0)
                setDescription(item.description || '')
                setReleasedYear(item.released_year || '')
                setCondition(item.condition || '')
                setRarity(item.rarity || '')
                setBoxType(item.box_type || '')
                setQuantity(item.quantity || 1)
                setPictures(item.pictures || [])
                setOriginalPrice(item.original_price || 0)
                setPaymentMethods(item.paymentMethods || [])
            }
        }).catch(error => {
            console.error("Error loading listing:", error)
            toast.error("Failed to load listing data")
        })

        scrollTo({
            top: 0
        })
    }, [cardId])

    useEffect(() => {
        let flag = true
        console.log("Validation check:", { pictures, title, price, description, releasedYear, quantity, paymentMethods })
        
        if (!pictures || pictures.length === 0) {
            console.log("Pictures validation failed")
            flag = false
        }
        if (!title || title.trim().length === 0) {
            console.log("Title validation failed")
            flag = false
        }
        if (!price || parseFloat(price) < 0.01) {
            console.log("Price validation failed:", price)
            flag = false
        }
        if (!description || description.trim().length === 0) {
            console.log("Description validation failed")
            flag = false
        }
        if (!releasedYear || parseInt(releasedYear) < 1900) {
            console.log("Released year validation failed:", releasedYear)
            flag = false
        }
        if (!quantity || parseInt(quantity) < 1) {
            console.log("Quantity validation failed:", quantity)
            flag = false
        }
        if (!paymentMethods || paymentMethods.length === 0) {
            console.log("Payment methods validation failed")
            flag = false
        }
        
        console.log("Final validation flag:", flag)
        setActiveCreate(flag)
    }, [pictures, title, price, description, releasedYear, quantity, paymentMethods])

    const generateSubCategoryOptions = (item) => {
        const catOptions = []

        item.subCategories.forEach((cat) => {
            catOptions.push({ value: cat, label: cat.toUpperCase() })
        })

        return catOptions
    }

    const onUpdateListing = async () => {
        try {
            const newPictures = pictures.reduce((acc, cur) => {
                if (cur !== null) acc.push(cur)
                return acc
            }, [])

            await updateListing({ 
                userId: user.uid, 
                username: userData.username, 
                releasedYear: releasedYear, 
                cardId: card.id, 
                pictures: newPictures, 
                title, 
                condition, 
                price, 
                category, 
                subCategory, 
                description, 
                brand, 
                releasedYear, 
                boxType, 
                quantity, 
                originalPrice, 
                paymentMethods 
            })
            
            toast.success("Listing updated successfully!")
            setShowConfirmModal(true)
        } catch (error) {
            console.error("Error updating listing:", error)
            toast.error("Failed to update listing. Please try again.")
        }
    }

    const addImage = (file, index) => {
        let temp = [...pictures]
        temp[index] = file
        setPictures(temp)
    }

    const handleFileRemove = (index) => {
        let temp = [...pictures]
        temp[index] = null
        setPictures(temp)
    }

    const removeImage = (index) => {
        let temp = [...pictures]
        temp.splice(index, 1)
        setPictures(temp)
    }

    const handleCloseConfirmModal = () => {
        setShowConfirmModal(false)
        navigate("/my-listings")
    }

    const handleCloseDeleteConfirmModal = async () => {
        await deleteListing(card.id)
        setShowDeleteConfirmModal(false)
        setShowDeleteConfirmedModal(true)
    }

    const handleDeleteConfirmed = () => {
        setShowDeleteConfirmedModal(false)
        navigate("/my-listings")
    }

    return (
        <div>
            <DialogConfirmation
                message="Your listing has been successfully updated!"
                open={showConfirmModal}
                onClose={handleCloseConfirmModal}
                buttonText="Done"
                title="Listing Updated"
                iconSrc="/assets/icons/icon-check-circle.svg"
                type="confirm"
            />
            <DialogConfirmation
                message="Are you sure you want to delete this listing?"
                open={showDeleteConfirmModal}
                onClose={handleCloseDeleteConfirmModal}
                buttonText="Yes Delete"
                title="Delete Listing?"
                iconSrc="/assets/icons/icon-info.svg"
                type="confirm"
            />
            <DialogConfirmation
                message="Your listing has been successfully deleted!"
                open={showDeleteConfirmedModal}
                onClose={handleDeleteConfirmed}
                buttonText="Done"
                title="Listing Deleted"
                iconSrc="/assets/icons/icon-check-circle.svg"
                type="confirm"
            />

            <Breadcrumb link='/my-listings' />
            <h1 className="mt-5 font-aero uppercase text-white leading-[1.2] text-[28px] lg:text-[32px]">
                Edit Listing
            </h1>

            <hr className="my-10 border-gray"></hr>

            <div className="w-full lg:flex justify-between gap-6">
                <span className="block uppercase text-primary text-[12px] max-lg:mb-8">Upload Imgs</span>

                <div>
                    <div className="flex mb-8 max-lg:overflow-x-auto -mt-3 pt-3 x-scrollbar gap-x-5">
                        {[0, 1, 2, 3, 4].map((item) =>
                            <UploadInput
                                className="w-[150px] min-w-[150px] object-cover h-[150px]"
                                uploadId={`edit-listing-${item}`}
                                onFileSelect={(file) => addImage(file, item)}
                                onFileRemove={handleFileRemove}
                                defaultPreview={pictures[item]}
                                id={item}
                            />
                        )}
                    </div>

                    <div className="w-fit">
                        {/* <label htmlFor="image-upload" className="text-white font-thin text-xs"> */}
                        <div className="flex items-center py-3 text-gray-100 text-xs">
                            <img src="/assets/icons/icon-upload.svg" alt="icon-upload" className="mr-2" />
                            PNG, JPG Up to 5 imgs
                        </div>
                        {/* </label> */}
                    </div>
                </div>
            </div>

            <hr className="my-10 border-gray"></hr>

            <div className="lg:flex justify-between gap-6">
                <span className="block uppercase text-primary text-[12px] max-lg:mb-8">Details</span>

                <div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-7 text-white lg:w-[830px]">
                        <label className="w-full  max-sm:col-span-2">
                            <span className="text-sm mb-3 block">
                                Category
                            </span>
                            <Dropdown
                                options={categories}
                                value={category}
                                placeholder="Select Category"
                                onChange={(e) => setCategory(e.target.value)}
                                className="border-[1px] border-gray text-xs rounded-[6px]"
                            />
                        </label>

                        <label className="w-full  max-sm:col-span-2">
                            <span className="text-sm mb-3 block">
                                Sub-Category
                            </span>
                            <Dropdown
                                options={subCategories}
                                value={subCategory}
                                placeholder="Select Category"
                                onChange={(e) => setSubCategory(e.target.value)}
                                className="border-[1px] border-gray text-xs rounded-[6px]"
                            />
                        </label>

                        <label className="w-full  col-span-2">
                            <span className="text-sm mb-3 block">Title</span>
                            <div className="border-[1px] border-gray text-xs rounded-[6px] placeholder:text-white/60 bg-transparent w-full py-4 px-6">
                                <input
                                    placeholder="Enter your title here"
                                    value={title}
                                    onChange={(e) => setTitle(e.currentTarget.value)}
                                    className="bg-transparent"
                                />
                            </div>
                        </label>

                        <label className="w-full col-span-1  max-sm:col-span-2">
                            <span className="text-sm mb-3 block">Price</span>

                            <div className="border-[1px] border-gray text-xs rounded-[6px] placeholder:text-white/60 bg-transparent w-full py-4 px-6">
                                <input
                                    placeholder="Add price"
                                    value={price || ''}
                                    onChange={(e) => setPrice(e.currentTarget.value)}
                                    className="bg-transparent"
                                />
                            </div>
                        </label>

                        <label className="w-full col-span-1  max-sm:col-span-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm mb-3 block">Your Cost</span>
                                <span className="text-xs mb-3 block text-[#777]">Won't Show On Listings*</span>
                            </div>

                            <div className="border-[1px] border-gray text-xs rounded-[6px] placeholder:text-white/60 bg-transparent w-full py-4 px-6">
                                <input
                                    placeholder="Add price"
                                    value={originalPrice || ''}
                                    onChange={(e) => setOriginalPrice(e.currentTarget.value)}
                                    className="bg-transparent"
                                />
                            </div>
                        </label>

                        <label className="w-full col-span-2">
                            <span className="text-sm mb-3 block">
                                Description
                            </span>

                            <div className="border-[1px] border-gray text-xs min-h-[100px] rounded-[6px] placeholder:text-white/60 bg-transparent w-full py-4 px-6">
                                <textarea
                                    placeholder="Add Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.currentTarget.value)}
                                    className="bg-transparent outline-none w-full"
                                />
                            </div>
                        </label>

                        <label className="w-full text-white date-input  max-sm:col-span-2">
                            <span className="text-sm mb-3 block">Card Release Year</span>

                            <div className="border-[1px] border-gray text-xs rounded-[6px] placeholder:text-white/60 bg-transparent w-full py-4 px-6">
                                <input
                                    placeholder="2025"
                                    value={releasedYear}
                                    type="number"
                                    onChange={(e) => setReleasedYear(e.currentTarget.value)}
                                    className="bg-transparent outline-none"
                                />
                            </div>
                        </label>

                        <label className="w-full  max-sm:col-span-2">
                            <span className="text-sm mb-3 block">
                                Condition
                            </span>
                            <Dropdown
                                options={conditions}
                                value={condition}
                                placeholder="Select Condition"
                                onChange={(e) => setCondition(e.target.value)}
                                className="border-[1px] border-gray text-xs rounded-[6px]"
                            />
                        </label>

                        {/* <label className="w-full col-span-2">
                            <span className="text-sm mb-3 block">
                                Rarity
                            </span>
                            <Dropdown
                                options={rarities}
                                value={rarity}
                                placeholder="Select Rarity"
                                onChange={(e) => setRarity(e.target.value)}
                                className="border-[1px] border-gray text-xs rounded-[6px]"
                            />
                        </label> */}

                        <label className="w-full col-span-2">
                            <span className="text-sm mb-3 block">
                                Box Type
                            </span>
                            <Dropdown
                                options={boxTypes}
                                value={boxType}
                                placeholder="Select Box Type"
                                onChange={(e) => setBoxType(e.target.value)}
                                className="border-[1px] border-gray text-xs rounded-[6px]"
                            />
                        </label>

                        <label className="w-full col-span-2">
                            <span className="text-sm mb-3 block">Available Amount</span>

                            <div className="border-[1px] border-gray text-xs rounded-[6px] placeholder:text-white/60 bg-transparent w-full py-4 px-6">
                                <input
                                    placeholder="Amount"
                                    value={quantity}
                                    type="number"
                                    onChange={(e) => setQuantity(e.currentTarget.value)}
                                    className="bg-transparent outline-none"
                                />
                            </div>
                        </label>

                        <label className="w-full col-span-2">
                            <span className="text-sm mb-3 block">Payment Methods</span>
                            <div className="flex gap-6">
                                {paymentOptions.map(option => (
                                    <label key={option.value} className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            value={option.value}
                                            checked={paymentMethods.includes(option.value)}
                                            onChange={e => {
                                                if (e.target.checked) {
                                                    setPaymentMethods([...paymentMethods, option.value])
                                                } else {
                                                    setPaymentMethods(paymentMethods.filter(v => v !== option.value))
                                                }
                                            }}
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        </label>
                    </div>

                    <hr className="mt-6 mb-8 border-gray"></hr>

                    <div className="flex max-sm:flex-col gap-3">
                        <button
                            className={`flex items-center sm:w-fit justify-center uppercase text-sm text-white px-10 py-3 bg-primary  transition-all ${!activeCreate ? 'opacity-60' : 'hover:text-primary hover:bg-white'}`}
                            onClick={onUpdateListing}
                            disabled={!activeCreate}
                        >
                            Update Listing
                        </button>

                        <button
                            className={`flex items-center sm:w-fit justify-center uppercase text-sm text-white px-10 py-3 bg-[#2F2F2F]  transition-all hover:bg-primary`}
                            onClick={() => setShowDeleteConfirmModal(true)}
                            disabled={!activeCreate}
                        >
                            Delete Listing
                        </button>
                        

                    </div>
                </div>
            </div >
        </div >
    )
}

export default MarketplaceEditListing
